import { isValidJson } from 'components/form/utilities'
import { getPathsFromTemplate } from 'lib/utilities/form/utilities'
import { omit } from 'lodash-es'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { CONTROLLED_FIELDS, updateTemplatePaths, useDataTemplate } from '../../../features/data/dataSlice'
import SectionDivider from '../SectionDivider'
import { LinkedPaths } from './Paths'
import { expandTemplate } from '../../utility'

const INDENT = 4

const Templating = ({ id: ids, component, isIncompleted }) => {
  const template = useDataTemplate(...ids)
  const dispatch = useDispatch()
  const textAreaRef = useRef()
  const [input, setInput] = useState(JSON.stringify(omit(template, CONTROLLED_FIELDS), null, INDENT))
  const isValid = useMemo(() => isValidJson(input), [input])
  const [inputInFocus, setInputInFocus] = useState(false)
  const prevFocusState = useRef(false)

  const templateString = useMemo(() => {
    return JSON.stringify(omit(template, CONTROLLED_FIELDS), null, INDENT)
  }, [template])

  const linkedPaths = useMemo(() => {
    return getPathsFromTemplate(template.template)
  }, [template])

  useEffect(() => {
    if (templateString) {
      setInput(templateString)
    }
  }, [templateString])

  const handleInputChange = (e) => {
    const { value } = e.target
    setInput(value || '')
  }

  const handleKeyDown = useCallback(
    (e) => {
      if (inputInFocus) {
        if (e.ctrlKey || e.metaKey) {
          switch (e.code) {
            case 'KeyS':
              e.preventDefault()
              storeIfValid()
              break
          }
        }
      }

      if (e.target === textAreaRef.current) {
        e.stopPropagation()
      }
    },
    [textAreaRef, storeIfValid, input, inputInFocus]
  )

  const storeIfValid = useCallback(() => {
    try {
      const data = JSON.parse(input)
      dispatch(updateTemplatePaths({ id: component.id, type: component.type, template: data }))
    } catch (e) {}
  }, [input, dispatch, component])

  const handleBlur = () => {
    prevFocusState.current = inputInFocus
    setInputInFocus(false)
    setInput(expandValue(input || '{}'))
  }

  const handleFocus = () => {
    prevFocusState.current = inputInFocus
    setInputInFocus(true)
  }

  const expandValue = (val) => {
    const trimmed = val.trim()
    try {
      JSON.parse(trimmed)
      return trimmed
    } catch (e) {
      return JSON.stringify({ template: expandTemplate(val) })
    }
  }

  useEffect(() => {
    if (isValid && prevFocusState.current && !inputInFocus) {
      storeIfValid()
    }
  }, [inputInFocus, prevFocusState])

  const templateStyle = {
    borderColor: isValid ? '#898989' : '#d7272d',
  }
  return (
    <SectionDivider sectionName="display + fill paths">
      <div className="dome-d-flex dome-flex-column dome-gap18">
        <div className="dome-d-flex dome-flex-column dome-gap6">
          <div>
            <div className="dome-d-flex dome-justify-between">
              <span className="dome-p3 dome-color-med-grey">template:*</span>
              {isIncompleted && (
                <div className="dome-d-flex dome-align-center dome-gap3">
                  <i className="fa-light fa-circle-exclamation dome-color-red-alert"></i>
                  <span className="dome-p3 dome-color-red-alert">required</span>
                </div>
              )}
            </div>
            <textarea
              ref={textAreaRef}
              className="scrollable"
              value={input || ''}
              style={templateStyle}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
            />
          </div>
          <LinkedPaths label="template paths" linkedPaths={linkedPaths} />
        </div>
      </div>
    </SectionDivider>
  )
}

export default Templating
